import { useState, useEffect, createContext, useContext } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create auth context to share auth state with children
export const AuthContext = createContext({ isAuthenticated: false, user: null });

export const useAuth = () => useContext(AuthContext);

export default function ProtectedRoute({ children }) {
  const [authState, setAuthState] = useState('checking'); // 'checking', 'authenticated', 'unauthenticated'
  const [user, setUser] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const checkAuth = async () => {
      const token = localStorage.getItem('nexintell_token');
      
      if (!token) {
        if (isMounted) setAuthState('unauthenticated');
        return;
      }

      try {
        // Verify the token is valid with the backend
        const response = await axios.get(`${API}/admin/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (isMounted) {
          setUser(response.data);
          setAuthState('authenticated');
        }
      } catch (error) {
        // Token is invalid or expired - clean up and redirect
        localStorage.removeItem('nexintell_token');
        localStorage.removeItem('nexintell_user');
        if (isMounted) setAuthState('unauthenticated');
      }
    };

    checkAuth();

    return () => { isMounted = false; };
  }, []);

  // Show loading spinner while checking authentication
  if (authState === 'checking') {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-[#ff4b2a] border-t-transparent mx-auto mb-4" />
          <p className="text-[#a1a1a1]">Verifying session...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (authState === 'unauthenticated') {
    return <Navigate to="/admin" replace />;
  }

  // Render the protected content with auth context
  return (
    <AuthContext.Provider value={{ isAuthenticated: true, user }}>
      {children}
    </AuthContext.Provider>
  );
}
